import { useEffect, useState } from "react";

import type { ConsentState } from "./rewardedAds";

interface TcfData {
  gdprApplies?: boolean;
  eventStatus?: string;
  listenerId?: number;
  purpose?: { consents?: Record<string, boolean> };
}

type TcfApi = (
  command: "addEventListener" | "removeEventListener",
  version: number,
  callback: (data: TcfData, success: boolean) => void,
  parameter?: number,
) => void;

interface GoogleConsentModeValues {
  adPersonalizationPurposeConsentStatus?: number;
  adStoragePurposeConsentStatus?: number;
  adUserDataPurposeConsentStatus?: number;
  analyticsStoragePurposeConsentStatus?: number;
}

type GoogleFcCallback =
  | { CONSENT_API_READY: () => void }
  | { CONSENT_MODE_DATA_READY: () => void };

interface GoogleFcApi {
  callbackQueue: GoogleFcCallback[];
  getGoogleConsentModeValues?: () => GoogleConsentModeValues;
  showRevocationMessage?: () => void;
}

declare global {
  interface Window {
    __tcfapi?: TcfApi;
    googlefc?: GoogleFcApi;
  }
}

export const CONSENT_SIGNAL_TIMEOUT_MS = 2_500;

function getGoogleFc(): GoogleFcApi {
  window.googlefc = window.googlefc || { callbackQueue: [] };
  window.googlefc.callbackQueue = window.googlefc.callbackQueue || [];
  return window.googlefc;
}

export function resolveTcfConsent(data: TcfData | null, success: boolean): ConsentState {
  if (!success || !data) return "unknown";
  if (data.gdprApplies === false) return "granted";
  if (data.gdprApplies !== true) return "unknown";
  const settled = data.eventStatus === "tcloaded" || data.eventStatus === "useractioncomplete";
  if (!settled) return "unknown";
  return data.purpose?.consents?.["1"] ? "granted" : "denied";
}

export function resolveGoogleConsentMode(values: GoogleConsentModeValues | null): ConsentState {
  if (!values) return "unknown";
  const advertisingPurposes = [
    values.adStoragePurposeConsentStatus,
    values.adUserDataPurposeConsentStatus,
    values.adPersonalizationPurposeConsentStatus,
  ];
  if (advertisingPurposes.some((value) => value === 2)) return "denied";
  if (advertisingPurposes.some((value) => value === undefined || value === 0)) return "unknown";
  return advertisingPurposes.every((value) => value === 1 || value === 3 || value === 4)
    ? "granted"
    : "unknown";
}

export function useWebAdConsent(): ConsentState {
  const [state, setState] = useState<ConsentState>("unknown");

  useEffect(() => {
    let active = true;
    let fallbackApplied = false;
    let listenerId: number | null = null;
    let subscribed = false;
    let fallbackTimer: number | null = null;
    const updateState = (nextState: ConsentState) => {
      if (!active || (fallbackApplied && nextState === "unknown")) return;
      if (nextState !== "unknown" && fallbackTimer !== null) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      setState(nextState);
    };
    const subscribe = () => {
      if (!active || subscribed || !window.__tcfapi) return;
      subscribed = true;
      window.__tcfapi("addEventListener", 2, (data, success) => {
        if (!active) return;
        if (typeof data.listenerId === "number") listenerId = data.listenerId;
        updateState(resolveTcfConsent(data, success));
      });
    };

    const googlefc = getGoogleFc();
    googlefc.callbackQueue.push({
      CONSENT_MODE_DATA_READY: () => {
        if (!active) return;
        updateState(resolveGoogleConsentMode(googlefc.getGoogleConsentModeValues?.() || null));
      },
    });
    subscribe();
    if (!subscribed) googlefc.callbackQueue.push({ CONSENT_API_READY: subscribe });
    fallbackTimer = window.setTimeout(() => {
      if (!active) return;
      fallbackApplied = true;
      fallbackTimer = null;
      setState((current) => current === "unknown" ? "denied" : current);
    }, CONSENT_SIGNAL_TIMEOUT_MS);

    return () => {
      active = false;
      if (fallbackTimer !== null) window.clearTimeout(fallbackTimer);
      if (listenerId !== null && window.__tcfapi) {
        window.__tcfapi("removeEventListener", 2, () => undefined, listenerId);
      }
    };
  }, []);

  return state;
}

export function showWebPrivacyOptions(): void {
  const googlefc = getGoogleFc();
  if (typeof googlefc.showRevocationMessage === "function") {
    googlefc.showRevocationMessage();
    return;
  }
  googlefc.callbackQueue.push({
    CONSENT_API_READY: () => window.googlefc?.showRevocationMessage?.(),
  });
}
